/**
 * Legacy info.csv (sim-phi style manifest; W1-07b formats-package; contract formats/info/types.ts `ReadInfoCsvFn`;
 * formats-and-packaging.md P§2.5).
 *
 * As Phira reads it with the Rust `csv` crate: comma-delimited, the first record is the header, records may have
 * any number of fields (flexible), fields may be double-quoted with `""` as an escaped quote, "\n" or "\r\n" end a
 * record, blank lines are skipped. Only the LAST record is used and at least one record is required ('csvEmpty',
 * fatal for Phira). Each header name is a key of the info.txt table (`applyLegacyKey`); cells that are empty after
 * trimming are skipped, the others are used as written.
 */

import { defaultInfo } from './infoYml';
import { applyLegacyKey } from './infoTxt';
import type { InfoYml, ManifestDiagnostic, ReadInfoCsvFn } from './types';

type MutableInfo = { -readonly [K in keyof InfoYml]: InfoYml[K] };

/** CSV records (RFC 4180 with the csv crate's leniency: quotes inside unquoted fields are literal). */
export function parseCsv(text: string): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let inQuotes = false;
  let quotedField = false;
  let fieldStarted = false;
  const endField = (): void => {
    record.push(field);
    field = '';
    quotedField = false;
    fieldStarted = false;
  };
  const endRecord = (): void => {
    // A blank line (one empty, unquoted field) is not a record.
    if (!(record.length === 0 && field === '' && !quotedField && !fieldStarted)) {
      endField();
      records.push(record);
    }
    record = [];
    field = '';
    quotedField = false;
    fieldStarted = false;
  };
  for (let i = 0; i < text.length; i++) {
    const c = text[i] ?? '';
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
      continue;
    }
    if (c === '"' && !fieldStarted) {
      inQuotes = true;
      quotedField = true;
      fieldStarted = true;
      continue;
    }
    if (c === ',') {
      endField();
      continue;
    }
    if (c === '\r' && text[i + 1] === '\n') continue;
    if (c === '\n' || c === '\r') {
      endRecord();
      continue;
    }
    field += c;
    fieldStarted = true;
  }
  endRecord();
  return records;
}

export const readInfoCsv: ReadInfoCsvFn = (text) => {
  const info = defaultInfo() as MutableInfo;
  const diagnostics: ManifestDiagnostic[] = [];
  const records = parseCsv(text);
  const header = records[0];
  const last = records.length > 1 ? records[records.length - 1] : undefined;
  if (header === undefined || last === undefined) {
    diagnostics.push({ code: 'csvEmpty', detail: '', phiraFatal: true });
    return { info, diagnostics };
  }
  header.forEach((key, i) => {
    const value = last[i];
    if (value === undefined || value.trim() === '') return;
    applyLegacyKey(info, key, value, diagnostics);
  });
  return { info, diagnostics };
};
